/* eslint-disable prettier/prettier */
import { Button } from 'app/components/ui/button';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useProductsSlice } from '../slice';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useToast } from 'app/components/ui/use-toast';
import { Toaster } from 'app/components/ui/toaster';
import { Form } from 'app/components/ui/form';
import ProductForm from '../Form';
import { ArrowLeft, Save, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from 'app/components/ui/card';

export default function EditProduct() {
  const form = useForm({
    defaultValues: {
      media: [],
      product_type: 1,
      variant_mode: '', // New field for single/variant selection
      tags: [],
      status: 2,
    },
  });
  const { useUpdateProductsMutation, useGetProductDetailsQuery } =
    useProductsSlice();
  const [updateProduct, { isError, isLoading, data }] =
    useUpdateProductsMutation();
  const [getProductDetails, { data: productDetails }] =
    useGetProductDetailsQuery();
  const { toast } = useToast();

  const [searchParams] = useSearchParams();

  const productId = searchParams.get('id');

  const navigate = useNavigate();

  useEffect(() => {
    getProductDetails(productId);
  }, [form, productId]);

  useEffect(() => {
    console.log('Product details loaded:', productDetails);
    if (productDetails) {
      // Create a copy of the data to avoid modifying the original object
      let d = { ...productDetails.data };
      console.log('Setting form values:', d);

      // Handle supplier data mapping
      if (d.supplier) {
        d.supplier_id = d.supplier.supplier_id;
        d.supplier_name = d.supplier.supplier_name;
      }

      Object.keys(d).forEach((key: any) => {
        form.setValue(key, d[key]);
      });
    }
  }, [productDetails]);

  const onSubmit = () => {
    console.log('Product form onSubmit called!');
    console.log('Form values:', form.getValues());
    console.log('Product ID:', productId);

    if (productId) {
      const formData = form.getValues();

      // Ensure we have the correct product_id for the update
      const updateData = {
        ...formData,
        product_id: parseInt(productId),
      };

      console.log('Sending update data:', updateData);
      updateProduct(updateData);
    } else {
      toast({
        title: 'Error',
        description: 'Product ID not found',
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    if (data) {
      toast({
        title: 'Success',
        description: data?.message || 'Product updated successfully',
        variant: 'default',
      });

      // Preserve tab context when redirecting back
      const currentUrl = new URL(window.location.href);
      const currentTab = currentUrl.searchParams.get('status');
      const currentProductType = currentUrl.searchParams.get('product-type');

      let redirectUrl = '/products';

      // Restore tab context
      if (currentTab && currentTab !== 'all') {
        redirectUrl += `?status=${currentTab}`;
      }

      // Restore product type context
      if (currentProductType) {
        const separator = redirectUrl.includes('?') ? '&' : '?';
        redirectUrl += `${separator}product-type=${currentProductType}`;
      }

      navigate(redirectUrl);
    }
  }, [data, toast, navigate]);

  useEffect(() => {
    if (isError) {
      toast({
        title: 'Error',
        description: isError?.message || 'Failed to update product',
        variant: 'destructive',
      });
    }
  }, [isError, toast]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button at Top */}
        <div className="mb-6">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => navigate('/products')}
            className="text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Products
          </Button>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-8"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.target instanceof HTMLInputElement) {
                console.log('Enter key pressed in product form input');
                e.preventDefault(); // Prevent form submission on Enter
              }
            }}
          >
            {/* Header */}
            <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-xl border border-white/20 dark:border-gray-700/50">
              <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
                  <div>
                    <h1 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-gray-100">
                      Edit Product
                    </h1>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      Update product information and settings
                    </p>
                  </div>

                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => navigate('/products')}
                      className="border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-500 transition-all duration-200"
                    >
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={isLoading}
                      className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white transition-all duration-200 disabled:opacity-50"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {isLoading ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Content */}
            <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm rounded-xl border border-white/20 dark:border-gray-700/50">
              <ProductForm />
            </div>
          </form>
        </Form>
        <Toaster />
      </div>
    </div>
  );
}
